#!/usr/bin/env node
/* eslint-disable @typescript-eslint/no-explicit-any */
import type { TransactionReceipt } from "@saberhq/solana-contrib";
import { Token, TokenAmount, u64 } from "@saberhq/token-utils";
import type { Connection, GetProgramAccountsFilter } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import BN from "bn.js";
import base58 from "bs58";
import { Command } from "commander";
import { printTable } from "console-table-printer";
import Decimal from "decimal.js";
import { exit } from "process";
import invariant from "tiny-invariant";

import * as pack from "../package.json";
import type {
  MinerData,
  MinerWrapper,
  QuarryData,
  QuarrySDK,
  RewarderData,
} from "../src";
import { Payroll, QUARRY_ADDRESSES, QUARRY_CODERS } from "../src";
import {
  catchFinallyExit,
  confirmTx,
  currentTs,
  getSigner,
  makeSDK,
  printObjectJSON,
  printObjectTable,
  receiptLog,
} from "./utils";

export const program = new Command().name("crema-mine");
program.version(pack.version);

/// The quarry sub commands
const quarryCmd = program
  .command("quarry")
  .description("The quarry_mine:Quarry sub commands");

quarryCmd.command("test").action(() => {
  const a = new Decimal("1.7").toDecimalPlaces(0);
  console.log(a.toString());
});

quarryCmd
  .command("new")
  .description("Create a quarry")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .requiredOption("-t, --famine-ts <famineTs>", "The quarry's famine timestamp")
  .requiredOption("-s, --share <share>", "The quarry's rewards share")
  .action((arg) => {
    const rewarder = new PublicKey(arg.rewarder);
    const mint = new PublicKey(arg.mint);
    const famineTs = new u64(arg.famineTs);
    const share = new u64(arg.share);
    newQuarry(rewarder, mint, share, famineTs)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

quarryCmd
  .command("info")
  .description("Get the quarry information")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    quarryInfo(r, m)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

quarryCmd
  .command("list")
  .description("Get the quarry list")
  .action(() => {
    catchFinallyExit(fetchQuarries());
  });

quarryCmd
  .command("update-share")
  .description("Set quarry's rewarders share")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .argument("<share>", "The quarry's rewarders share")
  .action((share, arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    const s = new u64(share);
    setRewardsShare(r, m, s)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

quarryCmd
  .command("set-famine")
  .description("Set quarry's famine timestamp")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .argument("<ts>", "The quarry's farmine timestamp")
  .action((share, arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    const s = new u64(share);
    setFamineTs(r, m, s)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

/// The rewarder sub commands
const rewarderCmd = program
  .command("rewarder")
  .description("The quarry_mine:Rewarder sub commands");

rewarderCmd
  .command("new")
  .description("Create a rewarder")
  .requiredOption("-w, --wrapper <wrapper>", "The mint wrapper addresss")
  .requiredOption(
    "-r, --annual-rate <annualRate>",
    "The rewarder annual rewards rate"
  )
  .action((arg) => {
    const w = new PublicKey(arg.wrapper);
    const r = new u64(arg.annualRate);
    newRewarder(w, r)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

rewarderCmd
  .command("info")
  .description("Get the rewarder information")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    fetchRewarder(r)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

rewarderCmd
  .command("list")
  .description("Get the rewarder list")
  .action(() => {
    fetchRewarders()
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

rewarderCmd
  .command("set-annual-rate")
  .description("Set rewarder annual rewards rate")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .argument("<rate>", "The new annual reward rate")
  .action((rate, arg) => {
    const r = new PublicKey(arg.rewarder);
    const rt = new u64(rate);
    setAnnualRate(r, rt)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

rewarderCmd
  .command("sync-quarry-rewards")
  .description("Synchronizes quarry rewards with the rewarder.")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption(
    "-m, --mint <mint>",
    "The mint public list, separated by ',' "
  )
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    const list = commaSeparatedPublicks(arg.mint);
    syncQuarryRewards(r, list)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

/// The mine sub commands
const minerCmd = program
  .command("miner")
  .description("The quarry_mine:Miner sub commands");

minerCmd
  .command("new")
  .description("Create a miner")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    newMiner(r, m)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minerCmd
  .command("info")
  .description("Get the miner information")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .requiredOption(
    "-a, --authority [authority]",
    "The miner's authority, default is signer"
  )
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    const a: PublicKey | null =
      arg.authority !== undefined ? new PublicKey(arg.authority) : null;
    minerInfo(r, m, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minerCmd
  .command("list")
  .description("Get the miner list")
  .option(
    "-a, --authority [authority]",
    "The miner's authority, default is signer"
  )
  .action((arg) => {
    const a: PublicKey | null =
      arg.authority !== undefined ? new PublicKey(arg.authority) : null;
    fetchMiners(a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minerCmd
  .command("stake")
  .description("Stake tokens to miner")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .argument("<amount>", "The stake amount")
  .action((amount, arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    const a = new u64(amount);
    stakeTokens(r, m, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minerCmd
  .command("claim")
  .description("Claim rewards from miner")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .action((arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    claim(r, m)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

minerCmd
  .command("withdraw")
  .description("Withdraw staked tokens from miner")
  .requiredOption("-r, --rewarder <rewarder>", "The rewarder address")
  .requiredOption("-m, --mint <mint>", "The stake token mint address")
  .argument("<amount>", "The stake amount")
  .action((amount, arg) => {
    const r = new PublicKey(arg.rewarder);
    const m = new PublicKey(arg.mint);
    const a = new u64(amount);
    withdrawTokens(r, m, a)
      .catch((err) => {
        console.log(err);
      })
      .finally(() => {
        exit(0);
      });
  });

async function newQuarry(
  rewarderKey: PublicKey,
  tokenMint: PublicKey,
  rewardShare: u64,
  famineTs: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, tokenMint);
  invariant(token !== null, "The token is null");
  const res = await rewarder.createQuarry({ token });
  const updateShareTx = rewarder.program.instruction.setRewardsShare(
    rewardShare,
    {
      accounts: {
        auth: {
          authority: sdk.provider.wallet.publicKey,
          rewarder: rewarderKey,
        },
        quarry: res.quarry,
      },
    }
  );
  const setFamineTsTx = rewarder.program.instruction.setFamine(famineTs, {
    accounts: {
      auth: {
        authority: rewarder.provider.wallet.publicKey,
        rewarder: rewarderKey,
      },
      quarry: res.quarry,
    },
  });
  res.tx.instructions.push(updateShareTx, setFamineTsTx);
  const receipt = await confirmTx(res.tx);
  printLog(receipt);

  return receipt;
}

async function quarryInfo(rewarderKey: PublicKey, tokenMint: PublicKey) {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, tokenMint);
  invariant(token !== null, "The token is null");
  const quarry = await rewarder.getQuarry(token);
  const info = quarry.quarryData;
  const ts = await currentTs(sdk.provider.connection);
  printObjectTable({
    quarry: quarry.key.toBase58(),
    rewardPerToken: quarry.payroll.calculateRewardPerToken(ts).toString(),
    ...info,
  });
}

async function setRewardsShare(
  rewarderKey: PublicKey,
  tokenMint: PublicKey,
  rewardersShare: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, tokenMint);
  invariant(token !== null, "The token is null");
  const quarry = await rewarder.getQuarry(token);
  const receipt = await confirmTx(quarry.setRewardsShare(rewardersShare));

  printLog(receipt);

  return receipt;
}

async function setFamineTs(
  rewarderKey: PublicKey,
  tokenMint: PublicKey,
  ts: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, tokenMint);
  invariant(token !== null, "The token is null");
  const quarry = await rewarder.getQuarry(token);
  const receipt = await confirmTx(quarry.setFamine(ts));

  printLog(receipt);

  return receipt;
}

async function fetchQuarries() {
  const sdk = makeSDK();
  const quarries = await getQuarries(sdk.provider.connection);
  const list: any[] = [];
  quarries.forEach((quarry) => {
    let endTime = "None";
    if (quarry.famineTs.lt(new BN("100000000000"))) {
      endTime = new Date(quarry.famineTs.toNumber() * 1000).toLocaleString();
    }
    list.push({
      quarry: quarry.address.toBase58(),
      rewarder: quarry.rewarder.toBase58(),
      tokenMint: quarry.tokenMintKey.toBase58(),
      famineTs: quarry.famineTs.toString(),
      lastUpdateTs: quarry.lastUpdateTs.toString(),
      endTime,
      rewardsShare: quarry.rewardsShare,
      totalTokensDeposited: quarry.totalTokensDeposited,
      annualRewardsRate: quarry.annualRewardsRate,
      numMiners: quarry.numMiners,
    });
  });
  printTable(list);
}

async function newRewarder(
  wrapper: PublicKey,
  annualRate: u64
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const createRes = await sdk.mine.createRewarder({ mintWrapper: wrapper });
  const setAnnualRateTx = sdk.mine.program.instruction.setAnnualRewards(
    annualRate,
    {
      accounts: {
        auth: {
          rewarder: createRes.key,
          authority: sdk.provider.wallet.publicKey,
        },
      },
    }
  );
  createRes.tx.instructions.push(setAnnualRateTx);
  const receipt = await confirmTx(createRes.tx);

  printLog(receipt);

  return receipt;
}

async function setAnnualRate(
  rewarderKey: PublicKey,
  newAnnualRate: u64
): Promise<TransactionReceipt> {
  const rewarder = await makeSDK().mine.loadRewarderWrapper(rewarderKey);
  const tx = rewarder.setAnnualRewards({ newAnnualRate });
  const receipt = await confirmTx(tx);

  printLog(receipt);

  return receipt;
}

async function fetchRewarder(rewarderKey: PublicKey) {
  const info = (await makeSDK().mine.loadRewarderWrapper(rewarderKey))
    .rewarderData;

  printObjectTable({
    RewarderKey: rewarderKey.toBase58(),
    ...info,
  });
}

function commaSeparatedPublicks(value: string): PublicKey[] {
  const list: PublicKey[] = [];
  value.split(",").forEach((v) => {
    list.push(new PublicKey(v));
  });
  return list;
}

async function syncQuarryRewards(
  rewarderKey: PublicKey,
  tokenMints: PublicKey[]
) {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const tx = await rewarder.syncQuarryRewards(tokenMints);
  const receipt = await confirmTx(tx);

  printLog(receipt);

  return receipt;
}

async function fetchRewarders() {
  const sdk = makeSDK();
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.Mine,
    {
      filters: [
        {
          memcmp: {
            offset: 0,
            bytes: base58.encode(
              QUARRY_CODERS.Mine.accounts.rewarder.discriminator
            ),
          },
        },
      ],
    }
  );
  const rewarders: any[] = [];
  accounts.forEach((accountInfo) => {
    const info = QUARRY_CODERS.Mine.coder.accounts.decode<RewarderData>(
      "Rewarder",
      accountInfo.account.data
    );
    rewarders.push({
      Rewarder: accountInfo.pubkey.toBase58(),
      rewardsTokenMint: info.rewardsTokenMint.toBase58(),
      Authority: info.authority.toBase58(),
      NumQuaries: info.numQuarries,
      annualRewardsRate: info.annualRewardsRate,
      totalRewardsShares: info.totalRewardsShares,
    });
  });

  printTable(rewarders);
}

async function newMiner(
  rewarderKey: PublicKey,
  mint: PublicKey
): Promise<TransactionReceipt> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, mint);
  invariant(token !== null);
  const quarry = await rewarder.getQuarry(token);
  const res = await quarry.createMiner();
  const receipt = await confirmTx(res.tx);
  printLog(receipt);

  return receipt;
}

async function minerInfo(
  rewarderKey: PublicKey,
  mint: PublicKey,
  authority: PublicKey | null
) {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, mint);
  invariant(token !== null);
  const quarry = await rewarder.getQuarry(token);
  if (authority === null) {
    authority = getSigner().publicKey;
  }
  const info = await quarry.getMiner(authority);
  if (info === null) {
    console.log("The miner not found.");
    return;
  }
  invariant(info !== null);
  const minerKey = await quarry.getMinerAddress(authority);
  const ts = await currentTs(sdk.provider.connection);
  const reward = quarry.payroll.calculateRewardsEarned(
    ts,
    info.balance,
    info.rewardsPerTokenPaid,
    info.rewardsEarned
  );
  printObjectTable({
    MinerKey: minerKey.toBase58(),
    PendingReward: reward.toString(),
    BlockTime: ts.toString(),
    ...info,
  });
}

interface MinerInfo extends MinerData {
  MinerKey: PublicKey;
  PendingReward: u64;
  BlockTime: u64;
}

export async function getMiners(
  sdk: QuarrySDK,
  authority: PublicKey | null
): Promise<Array<MinerInfo>> {
  const quarries = await getQuarries(sdk.provider.connection);
  const payrolls: Map<string, Payroll> = new Map();
  quarries.forEach((quarry) => {
    payrolls.set(
      quarry.address.toBase58(),
      new Payroll(
        quarry.famineTs,
        quarry.lastUpdateTs,
        quarry.annualRewardsRate,
        quarry.rewardsPerTokenStored,
        quarry.totalTokensDeposited
      )
    );
  });
  const ts = await currentTs(sdk.provider.connection);

  const filters: Array<GetProgramAccountsFilter> = [
    {
      memcmp: {
        offset: 0,
        bytes: base58.encode(QUARRY_CODERS.Mine.accounts.miner.discriminator),
      },
    },
  ];
  if (authority !== null) {
    filters.push({
      memcmp: {
        offset: 40,
        bytes: authority?.toBase58(),
      },
    });
  }
  const accounts = await sdk.provider.connection.getProgramAccounts(
    QUARRY_ADDRESSES.Mine,
    {
      filters,
    }
  );

  const miners: MinerInfo[] = [];

  accounts.forEach((accountInfo) => {
    const info = QUARRY_CODERS.Mine.coder.accounts.decode<MinerData>(
      "Miner",
      accountInfo.account.data
    );
    const payroll = payrolls.get(info.quarry.toBase58());
    invariant(
      payroll !== undefined,
      `The ${info.quarry.toBase58()} payroll not found`
    );
    miners.push({
      MinerKey: accountInfo.pubkey,
      PendingReward: payroll.calculateRewardsEarned(
        ts,
        info.balance,
        info.rewardsPerTokenPaid,
        info.rewardsEarned
      ),
      BlockTime: ts,
      ...info,
    });
  });

  return miners;
}

async function fetchMiners(authority: PublicKey | null) {
  const sdk = makeSDK();
  const miners = await getMiners(sdk, authority);
  const count = miners.length;
  let totalPendingRewards = new Decimal(0);
  miners.forEach((v) => {
    totalPendingRewards = totalPendingRewards.add(
      new Decimal(v.PendingReward.toString())
    );
  });
  printTable(miners);

  console.log("-------------------------------------------------------------");

  printObjectTable({
    TotalMiners: count,
    TokenPendingRewards: totalPendingRewards.toString(),
  });
}

interface QuarryInfo extends QuarryData {
  address: PublicKey;
}

export async function getQuarries(
  connection: Connection
): Promise<Array<QuarryInfo>> {
  const filters: Array<GetProgramAccountsFilter> = [
    {
      memcmp: {
        offset: 0,
        bytes: base58.encode(QUARRY_CODERS.Mine.accounts.quarry.discriminator),
      },
    },
  ];
  const accounts = await connection.getProgramAccounts(QUARRY_ADDRESSES.Mine, {
    filters,
  });
  const quarries: QuarryInfo[] = [];
  accounts.forEach((accountInfo) => {
    const info = QUARRY_CODERS.Mine.coder.accounts.decode<QuarryData>(
      "Quarry",
      accountInfo.account.data
    );
    quarries.push({
      address: accountInfo.pubkey,
      ...info,
    });
  });
  return quarries;
}

async function stakeTokens(
  rewarderKey: PublicKey,
  mint: PublicKey,
  amount: u64
): Promise<TransactionReceipt> {
  const miner = await minerWrapper(rewarderKey, mint);
  const receipt = await confirmTx(
    miner.stake(new TokenAmount(miner.quarry.token, amount))
  );
  printLog(receipt);

  return receipt;
}

async function claim(
  rewarderKey: PublicKey,
  mint: PublicKey
): Promise<TransactionReceipt> {
  const miner = await minerWrapper(rewarderKey, mint);
  const tx = await miner.claim();
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function withdrawTokens(
  rewarderKey: PublicKey,
  mint: PublicKey,
  amount: u64
): Promise<TransactionReceipt> {
  const miner = await minerWrapper(rewarderKey, mint);
  const tx = miner.withdraw(new TokenAmount(miner.quarry.token, amount));
  const receipt = await confirmTx(tx);
  printLog(receipt);

  return receipt;
}

async function minerWrapper(
  rewarderKey: PublicKey,
  mint: PublicKey
): Promise<MinerWrapper> {
  const sdk = makeSDK();
  const rewarder = await sdk.mine.loadRewarderWrapper(rewarderKey);
  const token = await Token.load(sdk.provider.connection, mint);
  invariant(token !== null);
  const quarry = await rewarder.getQuarry(token);
  return await quarry.getMinerActions(getSigner().publicKey);
}

function printLog(receipt: TransactionReceipt) {
  const events: Map<string, any> = new Map();
  QUARRY_CODERS.Mine.parseProgramLogEvents(
    receipt.response.meta?.logMessages ?? []
  ).forEach((event) => {
    events.set(event.name, event.data);
  });
  const e = Object.fromEntries(events);

  printObjectJSON(
    {
      ...receiptLog(receipt),
      events: e,
    },
    2
  );
}
